import { useEffect, useState } from "react";
import Layout from "../../components/Layout/Layout";
import moment from "moment";
// import { socket } from "../../socket";
import FaleAPI from "fale";
import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { useDebounce } from "../../hooks/debounce";
import Form from "react-bootstrap/Form";
import TabbedTable from "../../components/Layout/TabbedTable";

const badges = {
  New: "danger",
  Accepted: "info",
  Rejected: "danger",
  Paid: "success",
  Scheduled: "success",
  Preparing: "success",
  "Ready for Pickup": "success",
  "Ready for Delivery": "success",
  Complete: "success",
};

function Orders(props) {
  const tabs = [
    {
      label: "With Kitchen",
      statuses: [
        "New",
        "Paid",
        "Scheduled",
        "Preparing",
        "Ready for Pickup",
        "Ready for Delivery",
      ],
      color: "warning",
      title: "Orders with the Kitchen",
    },
    {
      label: "Accepted",
      statuses: ["Accepted"],
      color: "info",
      title: "Accepted Orders",
    },
    {
      label: "Complete",
      statuses: ["Complete"],
      color: "success",
      title: "Complete Orders",
    },
    {
      label: "Rejected / Cancelled",
      statuses: ["Rejected", "Cancelled"],
      color: "danger",
      title: "Rejected / Cancelled Orders",
    },
  ];
  const [orders, setOrders] = useState(null);
  const [notesByOrderId, setNotesByOrderId] = useState({});
  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const [statusCounts, setStatusCounts] = useState([]);
  const [deliveryFees, setDeliveryFees] = useState({});
  const [loadingOrders, setLoadingOrders] = useState({});

  const debounceRequest = useDebounce(() => {
    if (props.selectedKitchen) {
      FaleAPI.orders
        .getForKitchen(props.selectedKitchen?.uuid, selectedTab.statuses)
        .then((data) => {
          setStatusCounts(data.counts);
          setOrders(data.orders);
        });
    }
  });

  useEffect(() => {
    debounceRequest();
  }, [props.selectedKitchen, selectedTab, debounceRequest]);

  const getDeliveryFee = (id) =>
    deliveryFees[id] === undefined ? 5 : deliveryFees[id];

  const reloadOrders = (id) => () => {
    setLoadingOrders({ ...loadingOrders, [id]: false });
    debounceRequest();
  };

  const acceptOrder = (order) => {
    FaleAPI.orders
      .accept(
        order.id,
        order.method === "Delivery" ? getDeliveryFee(order.id) : "0"
      )
      .then(reloadOrders(order.id));
  };

  const prepareOrder = (id) => {
    FaleAPI.orders.prepare(id).then(reloadOrders(id));
  };

  const markOrderAsReady = (id) => {
    FaleAPI.orders.ready(id).then(reloadOrders(id));
  };

  const completeOrder = (id) => {
    FaleAPI.orders.complete(id).then(reloadOrders(id));
  };

  const rejectOrder = (id, reason) => {
    FaleAPI.orders.reject(id, reason).then(reloadOrders(id));
  };

  // socket.on("NEW_ORDER_FOR_OWNER", (data) => {
  //   setSelectedTab(tabs.find((tab) => tab.label === "With Kitchen"));
  //   debounceRequest();
  // });

  // socket.on("ORDER_PAID", (data) => {
  //   setSelectedTab(tabs.find((tab) => tab.label === "With Kitchen"));
  //   debounceRequest();
  // });

  const ready = {
    buttons: [
      {
        text: "Complete",
        color: "success",
        onClick: (order) => {
          completeOrder(order.id);
        },
      },
    ],
  };
  const workflow = {
    New: {
      buttons: [
        {
          text: "Accept",
          color: "success",
          onClick: (order) => {
            acceptOrder(order);
          },
        },
        {
          text: "Reject",
          color: "danger",
          onClick: (order) => {
            rejectOrder(order.id, notesByOrderId[order.id]);
          },
        },
      ],
    },
    Scheduled: {
      buttons: [
        {
          text: "Prepare",
          color: "success",
          onClick: (order) => {
            prepareOrder(order.id);
          },
        },
      ],
    },
    Preparing: {
      buttons: [
        {
          text: "Ready",
          color: "success",
          onClick: (order) => {
            markOrderAsReady(order.id);
          },
        },
      ],
    },
    "Ready for Pickup": ready,
    "Ready for Delivery": ready,
  };

  const columns = [
    { label: "Order No.", value: (order) => `#${order.order_number}` },
    {
      label: "Ordered Date",
      value: (order) => moment(order.created_on).format("DD/MM/YYYY"),
    },
    { label: "Ready By", value: (order) => order.ready_by },
    { label: "Customer", value: (order) => order.customer },
    {
      label: "Cart",
      value: (order) => (
        <OverlayTrigger
          overlay={
            <Tooltip placement="top">
              <ul>
                {order.items.map((item, index) => (
                  <li key={`product${index}`}>
                    {item.quantity} x {item.product.name}
                  </li>
                ))}
              </ul>
            </Tooltip>
          }
        >
          <span style={{ cursor: "pointer" }}>
            {order.product_count} Item
            {order.product_count === 1 ? "" : "s"}
          </span>
        </OverlayTrigger>
      ),
    },
    { label: "Total", value: (order) => `NZ$${order.total}` },
    {
      label: "Status",
      value: (order) => (
        <span
          className={`badge rounded-pill bg-${badges[order.status]} text-white`}
        >
          {order.status}
        </span>
      ),
    },
    {
      label: "Method",
      value: (order) => (
        <span
          className={`badge rounded-pill bg-${
            order.method === "Delivery" ? "dark" : "info"
          } text-white`}
        >
          <OverlayTrigger
            overlay={
              <Tooltip placement="top">
                {order.method === "Delivery"
                  ? `Deliver to: ${order.address}`
                  : "Your customers will be sent the pickup instructions you set in settings"}
              </Tooltip>
            }
          >
            <span style={{ cursor: "pointer" }}>{order.method}</span>
          </OverlayTrigger>
        </span>
      ),
    },
    {
      label: "Notes",
      value: (order) => (
        <span className="badge rounded-pill bg-success text-white">
          <OverlayTrigger
            overlay={
              <Tooltip placement="top">
                {order.notes || "Nothing to show"}
              </Tooltip>
            }
          >
            <span style={{ cursor: "pointer" }}>Notes</span>
          </OverlayTrigger>
        </span>
      ),
    },
    {
      label: "Delivery Fee",
      value: (order) =>
        order.method === "Delivery" &&
        order.status === "New" && (
          <Form.Select
            value={getDeliveryFee(order.id)}
            size="sm"
            style={{ width: "100px" }}
            onChange={(event) => {
              setDeliveryFees({
                ...deliveryFees,
                [order.id]: event.target.value,
              });
            }}
          >
            <option value="0">None</option>
            <option value="5">$5</option>
            <option value="7.5">$7.50</option>
            <option value="10">$10</option>
            <option value="12.5">$12.50</option>
            <option value="15">$15</option>
          </Form.Select>
        ),
    },
    {
      label: "",
      value: (order) =>
        workflow[order.status]?.buttons?.map((button) => (
          <button
            key={button.text}
            disabled={button.disabled}
            className={`btn btn-${button.color} btn-sm rounded ms-2`}
            onClick={() => {
              button.onClick(order);
              setLoadingOrders({ ...loadingOrders, [order.id]: true });
            }}
          >
            {button.text}
          </button>
        )),
    },
    {
      label: "Notes FOR Customer",
      value: (order) => {
        return (
          workflow[order.status]?.buttons?.length && (
            <textarea
              value={notesByOrderId[order.id]}
              onChange={(event) => {
                setNotesByOrderId({
                  ...notesByOrderId,
                  [order.id]: event.target.value,
                });
              }}
            ></textarea>
          )
        );
      },
    },
  ];

  return (
    <Layout {...props}>
      <div className="row gutters">
        <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12">
          <h4>Orders</h4>
          <TabbedTable
            type="Order"
            data={orders}
            rowIsLoading={(order) => loadingOrders[order.id]}
            tabs={tabs.map((t) => ({ ...t, columns }))}
            selectedTab={selectedTab}
            onTabClick={(tab) => () => {
              setOrders(null);
              setSelectedTab(tab);
              debounceRequest();
            }}
            getTabBadgeCount={(tab) =>
              statusCounts
                ?.filter?.((c) => tab.statuses.includes(c.status))
                .reduce?.(
                  (count, status) => count + parseInt(status.orderCount),
                  0
                )
            }
          />
        </div>
      </div>
    </Layout>
  );
}

export default Orders;
